import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './ProductCatalog.css';
import { getProductsBySubcategory, getTotalStock, isProductInStock } from '../../firebase/products';
import { getCategoryTree } from '../../firebase/categories';
import placeholderShirt from '../../assets/images/placeholder-shirt.png';

const LOW_STOCK_THRESHOLD = 10;

function getStockStatus(product) {
    if (product.inventory && Object.keys(product.inventory).length > 0) {
        const total = getTotalStock(product.inventory);
        if (total === null) return 'unknown';
        if (total === 0) return 'out';
        if (total <= LOW_STOCK_THRESHOLD) return 'low';
        return 'in';
    }
    if (typeof product.stockQuantity === 'number') {
        if (product.stockQuantity === 0) return 'out';
        if (product.stockQuantity <= LOW_STOCK_THRESHOLD) return 'low';
        return 'in';
    }
    return 'unknown';
}

function StockBadge({ stockStatus, productStatus }) {
    if (productStatus === 'inactive') return <span className="pc-badge pc-badge--unavailable">Unavailable</span>;
    if (stockStatus === 'out') return <span className="pc-badge pc-badge--out">Out of Stock</span>;
    if (stockStatus === 'low') return <span className="pc-badge pc-badge--low">Low Stock</span>;
    return null;
}

function SkeletonCard() {
    return (
        <div className="pc-card pc-card--skeleton">
            <div className="pc-card-img-wrap"><div className="pc-card-img pc-skeleton-img" /></div>
            <div className="pc-skeleton-name" />
            <div className="pc-skeleton-price" />
        </div>
    );
}

function ProductCatalog() {
    const navigate = useNavigate();
    const location = useLocation();
    
    const [tree, setTree] = useState([]);
    const [selectedMainId, setSelectedMainId] = useState(null);
    const [selectedSubId, setSelectedSubId] = useState(null);
    const [selectedSubmenu, setSelectedSubmenu] = useState('');
    const [hoveredProductId, setHoveredProductId] = useState(null);
    const [productsBySubcategory, setProductsBySubcategory] = useState({});
    const [loading, setLoading] = useState(false);
    const [catalogError, setCatalogError] = useState(null);

    useEffect(() => {
        let mounted = true;
        getCategoryTree({ activeOnly: true }).then((result) => {
            if (!mounted) return;
            if (result.error) {
                setCatalogError(result.error);
                return;
            }
            setTree(result.tree);
        });
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const categoryId = params.get('categoryId') || '';
        const subcategoryId = params.get('subcategoryId') || '';
        const submenu = params.get('submenu') || '';
        
        if (categoryId !== selectedMainId) {
            setSelectedMainId(categoryId);
            setSelectedSubId(subcategoryId);
            setSelectedSubmenu(submenu);
            setProductsBySubcategory({});
        } else if (subcategoryId !== selectedSubId) {
            setSelectedSubId(subcategoryId);
            setSelectedSubmenu(submenu);
        }
    }, [location.search, selectedMainId, selectedSubId]);

    const selectedMain = useMemo(() => {
        if (selectedMainId && tree.length > 0) {
            return tree.find((category) => category.id === selectedMainId) || tree[0];
        }
        return tree[0];
    }, [tree, selectedMainId]);
    
    const subcategories = useMemo(() => selectedMain?.subcategories || [], [selectedMain]);
    
    const resolvedSubId = selectedSubId || subcategories[0]?.id || '';
    const selectedSubcategory = useMemo(() => {
        if (!selectedMain) return null;
        if (resolvedSubId) {
            return subcategories.find((subcategory) => subcategory.id === resolvedSubId) || subcategories[0];
        }
        return subcategories[0];
    }, [subcategories, resolvedSubId, selectedMain]);
    
    const activeSubmenus = useMemo(() => (selectedSubcategory?.submenus || []).filter((submenu) => (submenu.status || 'active') === 'active'), [selectedSubcategory]);

    useEffect(() => {
        if (!selectedSubcategory?.name) return;
        if (productsBySubcategory[selectedSubcategory.name] !== undefined) return;
        let mounted = true;
        setLoading(true);
        setCatalogError(null);
        getProductsBySubcategory(selectedSubcategory.name, { includeInactive: false }).then((result) => {
            if (!mounted) return;
            if (result.error) setCatalogError(result.error);
            setProductsBySubcategory((prev) => ({ ...prev, [selectedSubcategory.name]: result.products || [] }));
            setLoading(false);
        });
        return () => { mounted = false; };
    }, [selectedSubcategory, productsBySubcategory]);

    const changeSubcategory = (subcategory) => {
        setSelectedSubId(subcategory.id);
        setSelectedSubmenu('');
        navigate(`/catalog?categoryId=${encodeURIComponent(selectedMain.id)}&subcategoryId=${encodeURIComponent(subcategory.id)}`, { replace: true });
    };

    const products = useMemo(() => {
        const list = (productsBySubcategory[selectedSubcategory?.name] || []).filter(isProductInStock);
        if (!selectedSubmenu) return list;

        return list.filter((product) => {
            if (Array.isArray(product.submenuTags) && product.submenuTags.length > 0) {
                return product.submenuTags.some(
                    (tag) => tag.toLowerCase() === selectedSubmenu.toLowerCase()
                );
            }
            const kw = selectedSubmenu.toLowerCase();
            const productName = (product.name || '').toLowerCase();
            const description = (product.description || '').toLowerCase();
            const nameWords = productName.split(/\s+/);
            const kwWords = kw.split(/\s+/);
            const nameMatch = kwWords.every(word => nameWords.some(pWord => pWord.includes(word) || word.includes(pWord)));
            if (nameMatch) return true;
            const similarity = (str1, str2) => {
                const longer = str1.length > str2.length ? str1 : str2;
                if (longer.length === 0) return 1.0;
                const editDistance = levenshtein(str1, str2);
                return (longer.length - editDistance) / longer.length;
            };
            const levenshtein = (s1, s2) => {
                const m = s1.length;
                const n = s2.length;
                const dp = [];
                for (let i = 0; i <= m; i++) {
                    dp[i] = [];
                    for (let j = 0; j <= n; j++) {
                        if (i === 0 || j === 0) dp[i][j] = j || i;
                        else if (s1[i-1] === s2[j-1]) dp[i][j] = dp[i-1][j-1];
                        else dp[i][j] = Math.min(dp[i-1][j] + 1, dp[i][j-1] + 1, dp[i-1][j-1] + 1);
                    }
                }
                return dp[m][n];
            };
            const productDesc = productName + ' ' + description;
            const sim = similarity(productDesc, kw);
            return sim > 0.6;
        });
    }, [productsBySubcategory, selectedSubcategory, selectedSubmenu]);

    const handleProductClick = (product) => {
        const stockStatus = getStockStatus(product);
        if (product.status === 'inactive' || stockStatus === 'out') return;
        navigate('/product-details', {
            state: {
                product: {
                    ...product,
                    category: selectedMain?.name || product.category,
                    categoryId: selectedMain?.id || product.categoryId,
                    subcategory: selectedSubcategory?.name || product.subcategory,
                    subcategoryId: selectedSubcategory?.id || product.subcategoryId,
                    image: product.imageUrl || (Array.isArray(product.images) && product.images[0]) || placeholderShirt,
                },
                breadcrumbTrail: [
                    { label: 'Home', path: '/' },
                    { label: selectedMain?.name || 'Catalog', path: `/catalog?categoryId=${selectedMain?.id || ''}` },
                    { label: selectedSubcategory?.name || 'Products', path: location.pathname + location.search },
                    { label: 'Product Details', path: '/product-details' },
                ],
            },
        });
    };

    return (
        <div className="pc-page">
            <div className="pc-wrapper">
                <div className="pc-layout">
                    {subcategories.length > 0 && (
                        <div className="pc-sidebar">
                            {subcategories.map((subcategory) => (
                                <button key={subcategory.id} onClick={() => changeSubcategory(subcategory)} className={`pc-sidebar-btn${selectedSubcategory?.id === subcategory.id ? ' active' : ''}`}>
                                    <span>{subcategory.name}</span>
                                    {subcategory.submenus.length > 0 && <span className="pc-soon-label">{subcategory.submenus.length}</span>}
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="pc-products">
                        <div className="pc-banner">
                            <h2 className="pc-banner-title">{(selectedSubcategory?.name || selectedMain?.name || 'Catalog').toUpperCase()}</h2>
                            <p className="pc-banner-subtitle">{selectedSubcategory?.description || 'Select your desired products to customize.'}</p>
                        </div>

                        {activeSubmenus.length > 0 && (
                            <div className="pc-brand-filter">
                                <button className={`pc-brand-btn${selectedSubmenu === '' ? ' active' : ''}`} onClick={() => setSelectedSubmenu('')}>All</button>
                                {activeSubmenus.map((submenu) => (
                                    <button key={submenu.id} className={`pc-brand-btn${selectedSubmenu === submenu.name ? ' active' : ''}`} onClick={() => setSelectedSubmenu(submenu.name)}>{submenu.name}</button>
                                ))}
                            </div>
                        )}

                        <div className="pc-grid">
                            {catalogError && <div className="pc-empty-state">Failed to load products. Please refresh and try again.</div>}
                            {loading && [1,2,3,4,5,6,7,8].map((item) => <SkeletonCard key={item} />)}
                            {!loading && products.map((product) => {
                                const stockStatus = getStockStatus(product);
                                const isBlocked = product.status === 'inactive' || stockStatus === 'out';
                                const productAttrs = product.selectedAttributes || product.attributes;
                                return (
                                    <div key={product.id} onClick={() => handleProductClick(product)} onMouseEnter={() => !isBlocked && setHoveredProductId(product.id)} onMouseLeave={() => setHoveredProductId(null)} className={['pc-card', hoveredProductId === product.id ? 'hovered' : '', isBlocked ? 'pc-card--blocked' : ''].filter(Boolean).join(' ')}>
                                        <div className="pc-card-img-wrap">
                                            <div className={`pc-card-img${isBlocked ? ' pc-card-img--dimmed' : ''}`}><img src={product.imageUrl || (Array.isArray(product.images) && product.images[0]) || placeholderShirt} alt={product.name} onError={(event) => { event.target.src = placeholderShirt; }} /></div>
                                            <StockBadge stockStatus={stockStatus} productStatus={product.status} />
                                            {!isBlocked && <div className="pc-card-cta"><span className="pc-card-cta-btn">Customize Now</span></div>}
                                        </div>
                                        <h3 className="pc-card-name">{product.name}</h3>
                                        <p className="pc-card-price">starts at <span className="pc-card-price-val">₱{(product.price || 0).toLocaleString()}</span></p>
                                        {productAttrs && Object.keys(productAttrs).length > 0 && (
                                            <div className="pc-attributes">
                                                {Object.entries(productAttrs).map(([key, attr]) => {
                                                    const val = typeof attr === 'object' && attr !== null ? attr.value : attr;
                                                    return (
                                                        <div key={key} className="pc-attr-row">
                                                            <span className="pc-attr-name">{key}:</span>
                                                            <span className="pc-attr-value">{val}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            {!loading && !catalogError && products.length === 0 && <div className="pc-empty-state">No products available at the moment.</div>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ProductCatalog;
